import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@app/store";
import {
  fetchTemplateById,
  createTemplate,
  updateTemplate,
  TemplateActions,
} from "./TemplateSlice";
import { fetchAffiliates } from "../affiliate/AffiliateSlice";
import { fetchUserProfile } from "../auth/authSlice";
import { TemplateMapper } from "./TemplateMapper";
import { TemplateOutputDTO } from "./types";
import { AffiliateOutputDTO } from "../affiliate/types";
import { DataForm } from "@components/ui";
import { FormSkeleton } from "@components/ui/FormSkeleton";
import { ApiError } from "@/types/api";
import { useNotification } from "@hooks/useNotification";
import { useAuth } from "@hooks/useAuth";
import { fetchCategoryDocuments } from "../categorydocument";

async function readFileAsDataURL(file: any) {
  let result_base64 = await new Promise((resolve) => {
    let fileReader = new FileReader();
    fileReader.onload = () => resolve(fileReader.result);
    fileReader.readAsDataURL(file);
  });
  return result_base64;
}

const TemplateForm: React.FC = (props: any) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentItem, loading } = useSelector(
    (state: RootState) => state.template
  );
  const { items: affiliates, loading: affiliatesLoading } = useSelector(
    (state: RootState) => state.affiliate
  );

  const {
    items: categories,
    loading: loading_categories,
    error: error_categories,
  } = useSelector((state: RootState) => {
    console.log(
      "🔍 Selector executando, loading:",
      state.categorydocument.loading
    );
    return state.categorydocument;
  });
  // Get current template info from auth state
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<Partial<TemplateOutputDTO>>({});
  const [formError, setFormError] = useState<ApiError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const notification = useNotification();

  const isLoadingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  const submitAttemptRef = useRef(false);

  // Make sure we have the current template data
  useEffect(() => {
    if (isAuthenticated && !user) {
      console.log("🔄 Fetching current template profile");
      dispatch(fetchUserProfile());
    }
  }, [dispatch, isAuthenticated, user]);

  // Debug output for current template
  useEffect(() => {
    console.log("🧪 Current authenticated template:", user);
  }, [user]);

  useEffect(() => {
    if (!id) {
      dispatch(TemplateActions.clearCurrentItem());
      setFormData({});
      setFields((prev: any) =>
        prev.map((field: any) => {
          if (field.name === "document_name") {
            return { ...field, value: "" };
          }
          return field;
        })
      );
    }
  }, [id, dispatch]);

  useEffect(() => {
    const loadData = async () => {
      if (isLoadingRef.current || initialLoadDoneRef.current) {
        return;
      }
      await dispatch(fetchCategoryDocuments({}));

      try {
        console.log("📡 Iniciando carregamento do Usuário:", id);
        isLoadingRef.current = true;

        // Load affiliates
        console.log("📡 TemplateForm - Carregando Afiliados");
        await dispatch(fetchAffiliates({}));

        if (id) {
          console.log("📡 TemplateForm - Carregando Usuário:", id);
          const resultAction = await dispatch(fetchTemplateById(id));

          if (fetchTemplateById.fulfilled.match(resultAction)) {
            console.log("✅ Usuário carregado com sucesso");
          } else {
            throw new Error("Erro ao carregar dados");
          }
        }
      } catch (error) {
        console.error("❌ Erro ao carregar Usuário:", error);
        notification.showError("Erro ao carregar dados");
        setFormError({
          error: "LOAD_ERROR",
          message: "Erro ao carregar dados",
          code: "LOAD_ERROR",
          statusCode: 500,
        });
      } finally {
        isLoadingRef.current = false;
        initialLoadDoneRef.current = true;
        setIsInitialLoad(false);
      }
    };

    loadData();

    return () => {
      console.log("🧹 Limpando estado do formulário");
      dispatch(TemplateActions.clearCurrentItem());
    };
  }, []);

  const handleFormChange = (newData: any) => {
    setFormData((prev) => {
      const updated = { ...prev, ...newData };
      // Preserve document_name if it exists in prev but not in newData (because DataForm doesn't track it)
      if (prev.document_name && !newData.document_name) {
        updated.document_name = prev.document_name;
      }
      return updated;
    });
  };

  useEffect(() => {
    if (
      formData.document &&
      typeof formData.document === "object" &&
      "name" in formData.document &&
      "size" in formData.document
    ) {
      const fileName = (formData.document as File).name;
      setFormData((prev) => ({
        ...prev,
        document_name: fileName,
      }));

      setFields((prev: any) =>
        prev.map((field: any) => {
          if (field.name === "document_name") {
            return { ...field, value: fileName };
          }
          return field;
        })
      );
    }
  }, [formData.document]);

  useEffect(() => {
    if (currentItem) {
      console.log(
        "📝 Template - Atualizando dados do formulário:",
        currentItem
      );
      setFormData({
        name: currentItem.name,
        category: currentItem.category,
        document: currentItem.document ?? "",
        document_name: currentItem.document_name,
        enterpriseId: currentItem.enterpriseId,
      });

      setFields((prev: any) =>
        prev.map((field: any) => {
          if (field.name === "document_name") {
            return { ...field, value: currentItem.document_name };
          }
          return field;
        })
      );
    }
  }, [currentItem]);

  useEffect(() => {
    console.log("atualizou", fields);
    setFields((currentFields: any) =>
      currentFields.map((item: any) => {
        if (item.name == "category") {
          item.options = categories
            .filter((item) => item.type == "template")
            .map((affiliate: AffiliateOutputDTO) => ({
              value: affiliate._id || "",
              label: affiliate.name || "Sem nome",
            }));
        }
        return item;
      })
    );
  }, [categories]);

  const [fields, setFields] = useState<any>([
    {
      name: "name",
      label: "Nome",
      type: "text",
      required: true,
      col: "100%",
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "document",
      label: "Documento",
      type: "document",
      required: true,
      col: "100%",
      accept: ".ppt,.pptx",
      validationError: formError?.validationErrors?.name?.message,
    },
    {
      name: "document_name",
      label: "Nome do Arquivo",
      type: "text",
      col: "100%",
      disabled: true,
    },
    {
      name: "category",
      label: "Categoria",
      type: "select",
      required: true,
      options: categories
        .filter((item) => item.type == "template")
        .map((affiliate: AffiliateOutputDTO) => ({
          value: affiliate._id || "",
          label: affiliate.name || "Sem nome",
        })),
      validationError: formError?.validationErrors?.category?.message,
    },
  ]);

  const handleSubmit = async (data2: Partial<TemplateOutputDTO>) => {
    if (submitAttemptRef.current) {
      notification.showWarning("Submissão em andamento, aguarde...");
      return;
    }
    let data: any = { ...data2 };

    console.log("🔍 handleSubmit - data2 received:", data2);
    console.log("🔍 handleSubmit - formData current state:", formData);

    // Ensure document_name is present (it might be missing from data2 if it's a disabled field)
    // Always prioritize formData for disabled fields
    if (formData.document_name) {
      console.log(
        "🔧 Restoring document_name from formData:",
        formData.document_name
      );
      data.document_name = formData.document_name;
    }

    // Ensure document is present (DataForm might not pass it correctly if state isn't synced)
    if (!data.document && formData.document) {
      console.log(
        "🔧 Restoring document from formData (type):",
        typeof formData.document
      );
      data.document = formData.document;
    }

    try {
      submitAttemptRef.current = true;
      setIsSubmitting(true);
      setFormError(null);

      // Robust check for File object
      const isFile =
        data?.document instanceof File ||
        (data?.document &&
          typeof data.document === "object" &&
          "name" in data.document &&
          "size" in data.document);

      if (isFile) {
        const file = data.document as File; // Cast is safe due to check
        console.log("📂 Processing file:", file.name);
        data.document_name = file.name;
        data.document = await readFileAsDataURL(file);
      } else {
        console.log(
          "📂 No new file to process. Document field type:",
          typeof data.document
        );
        delete data.document;
      }

      data.enterpriseId = user?.enterpriseId;

      if (!data.document_name) {
        console.warn("⚠️ document_name is missing before mapping!");
      }

      const mappedData = TemplateMapper.toDTO(data);
      console.log("📤 Enviando dados (Final Payload):", mappedData);

      const resultAction =
        id && props.onClose == undefined
          ? await dispatch(updateTemplate({ id, data: mappedData }))
          : await dispatch(createTemplate(mappedData));

      if (
        updateTemplate.rejected.match(resultAction) ||
        createTemplate.rejected.match(resultAction)
      ) {
        const error = resultAction.payload as ApiError;
        console.log(error);
        setFormError(error);
        // setFields(fields.map((item:any)=>{
        //   let data :any=error;
        //   data = data?.validationErrors[item.name]
        //   item.validationError = data?.message ?? ''
        //   return item;
        // }))
        notification.showError(error.message || "Erro ao salvar dados");
        return;
      }

      notification.showSuccess("Dados salvos com sucesso!");
      if (props.onClose) {
        props.onClose();
      } else {
        navigate("/templates");
      }
    } catch (error) {
      console.error("❌ TemplateForm - Erro inesperado:", error);
      notification.showError("Erro inesperado ao salvar os dados");
      setFormError({
        error: "UNKNOWN_ERROR",
        code: "UNKNOWN_ERROR",
        message: "Erro inesperado ao salvar os dados",
        statusCode: 500,
      });
    } finally {
      setIsSubmitting(false);
      submitAttemptRef.current = false;
    }
  };

  // Return nothing if template is a lawyer (will be redirected)

  if (isInitialLoad && (loading || affiliatesLoading)) {
    return <FormSkeleton fields={6} />;
  }

  return (
    <>
      {/* <Box sx={{ p: 3 }}> */}
      {/*  <Typography variant="h5" gutterBottom>
        {id ? "Editar Usuário" : "Novo Usuário"}
      </Typography>
       */}
      {/* // {currentTemplateRole === TemplateRole.MANAGER && ( */}
      {/* //   <Alert severity="info" sx={{ mb: 3 }}>
      //     Como gerente, você não pode criar usuários administradores nem alterar a empresa vinculada ao usuário.
      //   </Alert>
      // )} */}

      <DataForm
        key={id ? id : "new"}
        data={formData}
        onClose={props.onClose}
        isEdit={id != undefined}
        setFormDataParent={handleFormChange}
        fields={fields.filter((item: any) => {
          if (item.name == "document_name") {
            return formData.document_name;
          }
          return true;
        })}
        onSubmit={handleSubmit}
        basePath="templates"
        title={id ? "Editar Template" : "Novo Template"}
        error={formError}
        onClearError={() => setFormError(null)}
        disabled={isSubmitting}
      />
      {/* </Box> */}
      {/* // 
    // </div> */}
    </>
  );
};

export default TemplateForm;
